import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { tokens } from '../../../../global/theme/tokens';
import ExoAvatar from '../../ExoAvatar';
import UserStack from '../../UserStack';

// one counter of the card header
export const CapacityHeaderStat = ({ label, value, color }) => (
  <Box className="flex flex-col items-center">
    <Typography fontSize="9px" className="uppercase opacity-50">
      {label}
    </Typography>
    <Typography
      fontSize="13px"
      fontWeight={600}
      className="monospace"
      sx={{ color: color || 'inherit' }}
    >
      {value}
    </Typography>
  </Box>
);

// expandable project row without own data: counters come in as a list, the body as children
const CapacityProjectCard = ({
  project,
  isOpen,
  onToggle,
  // [{ key, label, value, color }] in render order
  stats = [],
  subtitle,
  teamMembers = [],
  // shown while the card is closed, where the children are shown while it is open
  preview,
  children,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const clientName = project.clientType
    ? project.clientType === 'company'
      ? project.client?.name
      : `${project.client?.lastName || ''} ${project.client?.firstName || ''}`
    : '';

  const avatar = project.image ? (
    <img
      src={project.image.temporaryUrl}
      alt=""
      className="w-7 h-7 rounded-full object-cover"
    />
  ) : (
    <ExoAvatar
      picture={project.client?.logo?.temporaryUrl}
      type={project.clientType}
      name={clientName}
      size="28px"
    />
  );

  return (
    // shrink-0: inside the scrolling list a card keeps its own height
    <Box
      className="rounded-xl overflow-hidden backdrop-blur-lg shrink-0"
      sx={{ backgroundColor: colors.glass }}
    >
      <Box
        onClick={onToggle}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-2 cursor-pointer"
        sx={{ ':hover': { backgroundColor: `${colors.text}08` } }}
      >
        {/* the row toggles too, so the click must not reach it twice */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="shrink-0"
        >
          <KeyboardArrowRightIcon
            sx={{
              fontSize: '20px',
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>

        {/* the picture is the shortcut into the project itself */}
        <Tooltip title={t('Open project')}>
          <Box
            className="shrink-0 flex rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/management/${project.id}`);
            }}
            sx={{
              ':hover': {
                outline: `2px solid ${colors.info}`,
                outlineOffset: '1px',
              },
            }}
          >
            {avatar}
          </Box>
        </Tooltip>

        {/* flex-basis 0: the row wraps, so a long name must be cut off instead
            of pushing the counters onto the next line */}
        <Box
          className="flex flex-col"
          sx={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}
        >
          {/* below md the number sits above the name, from md they share a baseline */}
          <Box className="flex flex-col md:flex-row md:items-baseline gap-x-2 min-w-0">
            {project.nr && (
              <Typography
                fontWeight={600}
                className="monospace opacity-50 shrink-0"
                sx={{ fontSize: { xs: '11px', md: '15px' } }}
              >
                {project.nr}
              </Typography>
            )}
            <Typography
              fontWeight={600}
              noWrap
              title={project.name}
              sx={{
                fontSize: { xs: '14px', md: '15px' },
                // while the row is a column the flex shorthand would zero the height
                flex: { xs: '0 1 auto', md: '1 1 0%' },
                minWidth: 0,
              }}
            >
              {project.name}
            </Typography>
          </Box>
          {Boolean(subtitle) && (
            <Typography fontSize="10px" className="opacity-60" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* on a phone the counters get their own row below the title */}
        {Boolean(stats.length || teamMembers.length) && (
          <Box className="flex flex-wrap items-center justify-between sm:justify-end gap-x-4 gap-y-1 w-full sm:w-auto shrink-0 order-last sm:order-none">
            {stats.map((stat) => (
              <CapacityHeaderStat
                key={stat.key || stat.label}
                label={stat.label}
                value={stat.value}
                color={stat.color}
              />
            ))}

            {Boolean(teamMembers.length) && (
              <Box
                className="shrink-0"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiAvatar-root': {
                    width: '24px !important',
                    height: '24px !important',
                    fontSize: '10px',
                  },
                }}
              >
                <UserStack users={teamMembers} max={5} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* the tasks stay readable without opening the phases below them */}
      {Boolean(preview) && !isOpen && (
        <Box className="flex flex-col gap-2 px-1 sm:px-3 pb-3">{preview}</Box>
      )}

      <Collapse in={isOpen} unmountOnExit>
        {/* the nested content needs every pixel on a phone */}
        <Box className="flex flex-col gap-2 px-1 sm:px-3 pb-3">{children}</Box>
      </Collapse>
    </Box>
  );
};

export default CapacityProjectCard;
